import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import {
  Button,
  LinearProgress,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';

import AppSnackbar from '../../common/AppSnackbar';
import { formatDate } from '../../common/formatter';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import SearchBox from '../../common/SearchBox';
import ProductCreateDialog from './dialogs/ProductCreateDialog';
import { useFindProducts } from './Product.hooks';
import { Product } from './Product.interface';
import ListMoreButtonMenu from '../../common/ListMoreButtonMenu';

const ProductListPage: React.FC = () => {
  const [search = '', setSearch] = useQueryParam('search', StringParam);
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);
  const [includeDeleted, setIncludeDeleted] = useQueryParam(
    'deleted',
    withDefault(BooleanParam, false)
  );
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const history = useHistory();

  const { productList, isLoading, loadingError, findProducts } =
    useFindProducts();

  useEffect(() => {
    findProducts({ search, page, limit, order, direction, includeDeleted });
  }, [page, limit, order, direction, includeDeleted, search, findProducts]);

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Products</h2>
        <SearchBox value={search} onSearch={setSearch} />
        <PageTemplateHeaderActions>
          <Button
            color='primary'
            variant='contained'
            className='button'
            onClick={() => setIsOpenDialog(true)}
          >
            Add Product
          </Button>
          <ListMoreButtonMenu>
            <ListItem
              button
              onClick={() =>
                setIncludeDeleted(includeDeleted ? undefined : true)
              }
            >
              <ListItemText
                primary={`${includeDeleted ? 'Hide' : 'Show'} Archived`}
              />
            </ListItem>
          </ListMoreButtonMenu>
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateCardOver>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'product.id'}
                    direction={getSortDirection('product.id')}
                    onClick={() => handleSort('product.id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>

                <TableCell>
                  <TableSortLabel
                    active={order === 'product.name'}
                    direction={getSortDirection('product.name')}
                    onClick={() => handleSort('product.name')}
                  >
                    Product
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'product.modified'}
                    direction={getSortDirection('product.modified')}
                    onClick={() => handleSort('product.modified')}
                  >
                    Last Modified
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={3} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {productList &&
                productList.data.map((product: Product) => (
                  <TableRow
                    hover
                    key={product.id}
                    className={product.deleted ? 'deleted' : ''}
                    onClick={() =>
                      history.push(`/portal/products/${product.id}`)
                    }
                  >
                    <TableCell>PRODUCT-{product.id}</TableCell>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>{formatDate(product.modified)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component='div'
          count={productList?.total || 0}
          rowsPerPage={limit}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={event => {
            setLimit(+event.target.value);
            setPage(0);
          }}
        />
      </PageTemplateCardOver>
      <AppSnackbar severity='error' open={Boolean(loadingError)}>
        There was an error loading the list of products.
      </AppSnackbar>
      {isOpenDialog && (
        <ProductCreateDialog
          open={isOpenDialog}
          onCancel={() => setIsOpenDialog(false)}
          onCreated={() => {
            setIsOpenDialog(false);
            findProducts(); // refresh data
          }}
        />
      )}
    </PageTemplate>
  );
};

export default ProductListPage;
